import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../apiconfig/api-config';
import NoRecordFound from '../common/no-record-found';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronRight, FiPlus, FiEye, FiDownload, FiFile } from 'react-icons/fi';
import { formatDateDDMMYY } from '../common/dateUtils';

const ROWS_PER_PAGE = 25;

const DocumentList = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewFile, setViewFile] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const navigate = useNavigate();

  // Collapsible state for orders and doc types
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expandedDocTypes, setExpandedDocTypes] = useState({});

  const toggleOrder = (orderId) => {
    setExpandedOrders((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };
  
  const toggleDocType = (orderId, docType) => {
    setExpandedDocTypes((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [docType]: !prev[orderId]?.[docType],
      },
    }));
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page when search changes
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      
      const res = await fetch(`${API_BASE_URL}/api/document/list?${params.toString()}`, {
        credentials: 'include',
      });
      
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const getFileName = (filePath) => filePath ? filePath.split('/').pop() : '';
  const getFileType = (filePath) => {
    if (!filePath) return '';
    const ext = filePath.split('.').pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext)) return 'image';
    if (["pdf"].includes(ext)) return 'pdf';
    return ext;
  };

  const handleViewFile = (filePath) => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7000';
    const url = `${apiBase}/${filePath.replace(/\\/g, '/')}`;
    if (getFileType(url) === 'pdf') {
      window.open(url, '_blank');
    } else {
      setViewFile(url);
    }
  };

  // Helper: Group records by purchase_contract_id and doc_type_name
  const groupRecords = (records) => {
    const grouped = {};
    records.forEach((doc) => {
      const orderId = doc.purchase_contract_id || 'No Order';
      const docType = doc.doc_type_name || 'Other';
      if (!grouped[orderId]) grouped[orderId] = {};
      if (!grouped[orderId][docType]) grouped[orderId][docType] = [];
      grouped[orderId][docType].push(doc);
    });
    return grouped;
  };

  // File type icon component
  const FileTypeIcon = ({ type }) => {
    const iconClass = "text-[15px] mr-2";
    switch(type) {
      case 'pdf':
        return <FiFile className={`${iconClass} text-red-500`} />;
      case 'image':
        return <FiFile className={`${iconClass} text-[#15803d]`} />;
      default:
        return <FiFile className={`${iconClass} text-gray-500`} />;
    }
  };
// Using the common formatDateDDMMYY function from dateUtils

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Document Management</h2>
          <p className="text-[11px] text-gray-500 mt-0.5">Browse and manage all documents</p>
        </div>
        <div className="flex gap-1 w-full sm:w-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search documents..."
              value={search}
              onChange={handleSearchChange}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-56 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
            onClick={() => navigate('/document/new')}
          >
            <FiPlus className="text-[12px]" /> Add Document
          </button>
        </div>
      </div>

      {/* Modal for file preview */}
      {viewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-4xl w-full mx-4 relative flex flex-col">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl bg-gray-100 hover:bg-gray-200 rounded-full w-8 h-8 flex items-center justify-center"
              onClick={() => setViewFile(null)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center mb-4">
              <FileTypeIcon type={getFileType(viewFile)} />
              <div>
                <h3 className="text-[15px] font-semibold text-gray-800 break-all">{getFileName(viewFile)}</h3>
                <div className="text-[12px] text-gray-500 uppercase tracking-wider">Type: {getFileType(viewFile)}</div>
              </div>
            </div>
            <div className="w-full flex justify-center items-center min-h-[300px] max-h-[70vh] border border-gray-200 rounded-lg bg-gray-50 p-4">
              {getFileType(viewFile) === 'pdf' ? (
                <iframe 
                  src={viewFile} 
                  title="PDF File" 
                  className="w-full h-full rounded border-none shadow-inner" 
                />
              ) : getFileType(viewFile) === 'image' ? (
                <img 
                  src={viewFile} 
                  alt="Uploaded File" 
                  className="max-w-full max-h-[65vh] mx-auto object-contain rounded shadow-sm" 
                />
              ) : viewFile ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <FiFile className="text-6xl text-gray-400 mb-4" />
                  <a 
                    href={viewFile} 
                    download={getFileName(viewFile)} 
                    className="flex items-center gap-2 text-[#15803d] hover:text-[#15803d] font-medium text-[13px]"
                  >
                    <FiDownload /> Download File
                  </a>
                </div>
              ) : (
                <div className="text-red-500">File not found or unsupported format.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Data table */}
      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p className="text-[#15803d] text-[13px]">Loading documents...</p>
        </div>
      ) : error ? (
        <div className="text-center py-6 text-red-600 text-[13px] bg-red-50 rounded-lg border border-red-100">
          {error} - Please try again later
        </div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No document records found." />
      ) : (
        <div className="space-y-4">
          {/* Nested Collapsible UI */}
          {Object.entries(groupRecords(records)).map(([orderId, docTypes]) => {
            // Get order details from the first document in the group
            const firstDoc = Object.values(docTypes)[0][0];
            const orderDetails = firstDoc ? {
              purchase_contract_no: firstDoc.purchase_contract_no,
              order_type: firstDoc.order_type,
              order_date: firstDoc.order_date,
              quantity_mt: firstDoc.quantity_mt,
              unit_price_usd: firstDoc.unit_price_usd,
              incoterm: firstDoc.incoterm,
              status: firstDoc.order_status,
            } : {};
            
            return (
              <div key={orderId} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs">
                {/* Order header */}
                <button
                  className={`flex items-center justify-between w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                    expandedOrders[orderId] ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => toggleOrder(orderId)}
                  aria-expanded={!!expandedOrders[orderId]}
                >
                  <div className="flex items-center">
                    {expandedOrders[orderId] ? (
                      <FiChevronDown className="text-[#15803d] mr-2 text-[15px]" />
                    ) : (
                      <FiChevronRight className="text-[#15803d] mr-2 text-[15px]" />
                    )}
                    <div>
                      <span className="font-semibold text-gray-800">Order #{orderId}</span>
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-[#15803d] text-[12px] rounded-full">
                        {Object.keys(docTypes).length} document types
                      </span>
                    </div>
                  </div>
                  <div className="text-[13px] text-gray-500">
                    {formatDateDDMMYY(orderDetails.order_date)}

                  </div>
                </button>

               {/* Enhanced Order details summary */}
<div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50">
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4 text-[13px]">
    
    <div>
      <p className="text-gray-600 font-semibold">Contract No:</p>
      <p className="text-gray-900">{orderDetails.purchase_contract_no || '-'}</p>
    </div>

    <div>
      <p className="text-gray-600 font-semibold">Type:</p>
      <p className="text-gray-900">{orderDetails.order_type || '-'}</p>
    </div>

    <div>
      <p className="text-gray-600 font-semibold">Qty (MT):</p>
      <p className="text-gray-900">{orderDetails.quantity_mt || '-'}</p>
    </div>

    <div>
      <p className="text-gray-600 font-semibold">Price:</p>
      <p className="text-gray-900">
        {orderDetails.unit_price_usd ? `₹${orderDetails.unit_price_usd}` : '-'}
      </p>
    </div>

    <div>
      <p className="text-gray-600 font-semibold">Incoterm:</p>
      <p className="text-gray-900">{orderDetails.incoterm || '-'}</p>
    </div>

    <div>
      <p className="text-gray-600 font-semibold">Status:</p>
      <p className="text-gray-900 flex items-center">
        <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${
          orderDetails.status === 'Completed' ? 'bg-green-500' : 
          orderDetails.status === 'Pending' ? 'bg-yellow-500' : 'bg-gray-400'
        }`}></span>
        {orderDetails.status || '-'}
      </p>
    </div>

  </div>
</div>


                {expandedOrders[orderId] && (
                  <div className="divide-y divide-gray-100">
                    {Object.entries(docTypes).map(([docType, docs]) => (
                      <div key={docType} className="p-4 hover:bg-gray-50 transition-colors">
                        <button
                          className="flex items-center justify-between w-full text-left mb-2"
                          onClick={() => toggleDocType(orderId, docType)}
                          aria-expanded={!!expandedDocTypes[orderId]?.[docType]}
                        >
                          <div className="flex items-center">
                            {expandedDocTypes[orderId]?.[docType] ? (
                              <FiChevronDown className="text-[#15803d] mr-2" />
                            ) : (
                              <FiChevronRight className="text-[#15803d] mr-2" />
                            )}
                            <span className="font-medium text-gray-800">{docType}</span>
                            <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-[12px] rounded-full">
                              {docs.length} documents
                            </span>
                          </div>
                          <div className="text-[13px] text-gray-500">
                            Last updated: {docs[0].uploaded_at ? formatDateDDMMYY(docs[0].uploaded_at) : 'N/A'}
                          </div>
                        </button>

                        {expandedDocTypes[orderId]?.[docType] && (
                          <div className="ml-8 mt-2 overflow-x-auto">
                            <table className="min-w-full text-left border-collapse">
                              <thead className="bg-[#f4f4f4] border-b border-gray-300">
                                <tr>
                                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Document ID</th>
                                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">File</th>
                                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Uploaded</th>
                                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {docs.map((item) => (
                                  <tr key={item.doc_id} className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{item.doc_id}</td>
                                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <FileTypeIcon type={getFileType(item.file_path)} />
                                        <span className="truncate max-w-xs">
                                          {item.file_path ? getFileName(item.file_path) : 'No file attached'}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">
                                      {formatDateDDMMYY(item.uploaded_at)}
                                    </td>
                                    <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 text-center">
                                      <button
                                        className={`text-[12px] p-1.5 rounded-sm transition-colors border inline-block ${
                                          item.file_path 
                                            ? 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200'
                                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        }`}
                                        onClick={() => item.file_path && handleViewFile(item.file_path)}
                                        disabled={!item.file_path}
                                        title={item.file_path ? "View File" : "No file available"}
                                      >
                                        <FiEye className="text-[14px]" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-3 text-[12px]">
          <div className="text-gray-600 mb-2 sm:mb-0">
            Showing <span className="font-semibold text-gray-900">{((page - 1) * ROWS_PER_PAGE) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * ROWS_PER_PAGE, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> records
          </div>
          <div className="inline-flex shadow-sm rounded-sm">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-l-sm transition-colors"
            >
              Previous
            </button>
            <div className="px-3 py-1 border-t border-b border-gray-300 bg-gray-50 text-gray-900 font-medium">
              {page} / {totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-r-sm transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;
